import { html, nothing } from "lit";
import { icons } from "../icons.ts";
import { formatToolDetail, resolveToolDisplay } from "../tool-display.ts";
import type { ChatSidebarBlock, ChatSidebarContent, ToolCard } from "../types/chat-types.ts";
import { TOOL_INLINE_THRESHOLD } from "./constants.ts";
import { extractTextCached } from "./message-extract.ts";
import { isToolResultMessage } from "./message-normalizer.ts";
import { formatToolOutputForSidebar, getTruncatedPreview } from "./tool-helpers.ts";
import { detectJsonPreview, jsonSummaryLabel } from "./transcript.ts";

export function extractToolCards(message: unknown): ToolCard[] {
  const m = message as Record<string, unknown>;
  const content = normalizeContent(m.content);
  const cards: ToolCard[] = [];
  const fallbackName =
    (typeof m.toolName === "string" && m.toolName) ||
    (typeof m.tool_name === "string" && m.tool_name) ||
    "tool";
  const hasTopLevelToolId =
    (typeof m.toolCallId === "string" && Boolean(m.toolCallId.trim())) ||
    (typeof m.tool_call_id === "string" && Boolean(m.tool_call_id.trim()));

  for (const item of content) {
    const kind = (typeof item.type === "string" ? item.type : "").trim().toLowerCase();
    const isToolCall =
      ["toolcall", "tool_call", "tooluse", "tool_use"].includes(kind) ||
      (typeof item.name === "string" && item.arguments != null);
    if (isToolCall) {
      cards.push({
        kind: "call",
        name: (item.name as string) ?? fallbackName,
        args: coerceArgs(item.arguments ?? item.args ?? item.input),
      });
    }
  }

  for (const item of content) {
    const kind = (typeof item.type === "string" ? item.type : "").trim().toLowerCase();
    if (kind !== "toolresult" && kind !== "tool_result" && kind !== "tool_result_error") {
      continue;
    }
    const text = extractToolText(item);
    const name = typeof item.name === "string" && item.name.trim() ? item.name : fallbackName;
    cards.push({ kind: "result", name, text });
  }

  const fallbackText = extractTextCached(message) ?? undefined;
  if (
    (isToolResultMessage(message) || (hasTopLevelToolId && fallbackText)) &&
    !cards.some((card) => card.kind === "result")
  ) {
    cards.push({ kind: "result", name: fallbackName, text: fallbackText });
  }

  return cards;
}

export function buildToolCardSidebarContent(card: ToolCard): ChatSidebarContent {
  const display = resolveToolDisplay({ name: card.name, args: card.args });
  const detail = formatToolDetail(display);
  const hasText = Boolean(card.text?.trim());

  if (card.kind === "call") {
    if (typeof card.args === "string") {
      const trimmed = card.args.trim();
      const jsonResult = detectJsonPreview(trimmed);
      if (jsonResult) {
        return {
          kind: "structured",
          title: display.label,
          blocks: [
            {
              type: "json",
              data: jsonResult.parsed,
              label: detail || jsonSummaryLabel(jsonResult.parsed),
            },
          ],
          rawText: formatToolOutputForSidebar(card.args),
        };
      }
      return {
        kind: "structured",
        title: display.label,
        blocks: [
          {
            type: "code",
            content: card.args,
            language: undefined,
            label: detail || "Arguments",
          },
        ],
        rawText: card.args,
      };
    }
    if (card.args !== undefined) {
      const rawArgs = JSON.stringify(card.args);
      return {
        kind: "structured",
        title: display.label,
        blocks: [
          {
            type: "json",
            data: card.args,
            label: detail || jsonSummaryLabel(card.args),
          },
        ],
        rawText: formatToolOutputForSidebar(rawArgs),
      };
    }
    return {
      kind: "structured",
      title: display.label,
      blocks: [
        {
          type: "markdown",
          content: `${detail ? `**Command:** \`${detail}\`\n\n` : ""}*No arguments.*`,
        },
      ],
      rawText: null,
    };
  }

  if (hasText) {
    const trimmed = card.text!.trim();
    const jsonResult = detectJsonPreview(trimmed);
    if (jsonResult) {
      return {
        kind: "structured",
        title: display.label,
        blocks: [
          {
            type: "json",
            data: jsonResult.parsed,
            label: detail || jsonSummaryLabel(jsonResult.parsed),
          },
        ],
        rawText: formatToolOutputForSidebar(card.text!),
      };
    }
    return {
      kind: "structured",
      title: display.label,
      blocks: [
        {
          type: "code",
          content: card.text!,
          language: undefined,
          label: detail || undefined,
        },
      ],
      rawText: formatToolOutputForSidebar(card.text!),
    };
  }

  return {
    kind: "structured",
    title: display.label,
    blocks: [
      {
        type: "markdown",
        content: `${detail ? `**Command:** \`${detail}\`\n\n` : ""}*No output — tool completed successfully.*`,
      },
    ],
    rawText: null,
  };
}

export function buildToolClusterSidebarContent(
  summary: string,
  cards: ToolCard[],
  options?: {
    summaryLabel?: string;
    totalTools?: number;
  },
): ChatSidebarContent {
  const blocks: ChatSidebarBlock[] = [];
  const rawParts: string[] = [];
  const summaryLine = [
    options?.totalTools ? `${options.totalTools} 个工具` : null,
    options?.summaryLabel ? options.summaryLabel : null,
  ]
    .filter(Boolean)
    .join(" · ");

  blocks.push({
    type: "markdown",
    content: summaryLine ? `**${summary}**\n\n${summaryLine}` : `**${summary}**`,
  });

  for (const card of cards) {
    const content = buildToolCardSidebarContent(card);
    if (typeof content === "string") {
      rawParts.push(content);
      blocks.push({ type: "markdown", content });
      continue;
    }
    blocks.push(...content.blocks);
    if (content.rawText?.trim()) {
      rawParts.push(`## ${content.title ?? summary}\n\n${content.rawText}`);
    }
  }

  return {
    kind: "structured",
    title: summary,
    blocks,
    rawText: rawParts.length > 0 ? rawParts.join("\n\n") : null,
  };
}

export function renderToolCardSidebar(card: ToolCard, onOpenSidebar?: (content: ChatSidebarContent) => void) {
  const display = resolveToolDisplay({ name: card.name, args: card.args });
  const detail = formatToolDetail(display);
  const hasText = Boolean(card.text?.trim());

  const canClick = Boolean(onOpenSidebar);
  const handleClick = canClick ? () => onOpenSidebar!(buildToolCardSidebarContent(card)) : undefined;

  const isShort = hasText && (card.text?.length ?? 0) <= TOOL_INLINE_THRESHOLD;
  const showCollapsed = hasText && !isShort;
  const showInline = hasText && isShort;
  const isEmpty = !hasText;

  return html`
    <div
      class="chat-tool-card ${canClick ? "chat-tool-card--clickable" : ""}"
      @click=${handleClick}
      role=${canClick ? "button" : nothing}
      tabindex=${canClick ? "0" : nothing}
      @keydown=${
        canClick
          ? (e: KeyboardEvent) => {
              if (e.key !== "Enter" && e.key !== " ") {
                return;
              }
              e.preventDefault();
              handleClick?.();
            }
          : nothing
      }
    >
      <div class="chat-tool-card__header">
        <div class="chat-tool-card__title">
          <span class="chat-tool-card__icon">${icons[display.icon]}</span>
          <span>${display.label}</span>
        </div>
        ${
          canClick
            ? html`<span class="chat-tool-card__action">${card.kind === "call" || hasText ? "View" : ""} ${icons.check}</span>`
            : nothing
        }
        ${isEmpty && !canClick ? html`<span class="chat-tool-card__status">${icons.check}</span>` : nothing}
      </div>
      ${detail ? html`<div class="chat-tool-card__detail">${detail}</div>` : nothing}
      ${
        isEmpty
          ? html`
              <div class="chat-tool-card__status-text muted">Completed</div>
            `
          : nothing
      }
      ${
        showCollapsed
          ? html`<div class="chat-tool-card__preview mono">${getTruncatedPreview(card.text!)}</div>`
          : nothing
      }
      ${showInline ? html`<div class="chat-tool-card__inline mono">${card.text}</div>` : nothing}
    </div>
  `;
}

function normalizeContent(content: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(content)) {
    return [];
  }
  return content.filter(Boolean) as Array<Record<string, unknown>>;
}

function coerceArgs(value: unknown): unknown {
  if (typeof value !== "string") {
    return value;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return value;
  }
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
    return value;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function extractToolText(item: Record<string, unknown>): string | undefined {
  if (typeof item.text === "string") {
    return item.text;
  }
  if (typeof item.content === "string") {
    return item.content;
  }
  if (typeof item.output === "string") {
    return item.output;
  }
  if (item.output !== undefined) {
    try {
      return JSON.stringify(item.output, null, 2);
    } catch {
      return String(item.output);
    }
  }
  return undefined;
}
